/**
 * Diacritic-insensitive normalization for search ("lucno" must find "Lučno") —
 * DAILY_ENTRY_INTERACTION_SPEC.md §5. NFD strips combining diacritics (č/ć/š/ž);
 * đ/Đ have no canonical decomposition in Unicode so they're mapped explicitly.
 */
export function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "dj")
    .replace(/Đ/g, "Dj")
    .toLowerCase()
    .trim();
}

export function fuzzyIncludes(haystack: string, needle: string): boolean {
  if (needle.trim() === "") return true;
  return normalizeForSearch(haystack).includes(normalizeForSearch(needle));
}

/**
 * Minimal shape `rankSearchMatches` needs — deliberately a plain structural
 * type (not imported from SearchCombobox) so this module has no dependency
 * on the UI layer. `ComboboxOption` in shared-ui/primitives is a superset
 * (adds `id`) that satisfies this automatically.
 */
export interface RankableSearchItem {
  label: string;
  description?: string;
  /** Business code (Worker.employeeCode / Product.code / Operation.code) — string only, never a parsed number. */
  businessCode?: string | null;
}

/**
 * Case-insensitive TEXT comparison for business codes — trims and
 * lowercases only. Deliberately NOT `normalizeForSearch` (no diacritic
 * folding — codes aren't expected to contain diacritics) and NEVER
 * `Number()` — "007" and "7" must stay textually distinct
 * (NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md §6).
 */
function normalizeCode(value: string): string {
  return value.trim().toLowerCase();
}

const DIGITS_ONLY = /^\d+$/;

/**
 * Strips leading zeroes for COMPARISON only (never touches a stored value —
 * `formatCodeAndName`/the DB always keep the padded form). Keeps at least
 * one digit ("000" -> "0"). Pure string manipulation, never `Number()` — so
 * this stays exact regardless of code length or leading-zero count
 * (NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md §17-18).
 */
function stripLeadingZeros(digits: string): string {
  return digits.replace(/^0+(?=\d)/, "");
}

/**
 * True when `code` and `query` are both digits-only and represent the same
 * number once leading zeroes are stripped from each side independently —
 * e.g. "5" and "005" ("15" and "015", "23" and "00023"). This is what lets
 * an operator type the short form of a fixed-width canonical code without
 * knowing/typing its zero-padding. Never applies to non-numeric codes
 * (e.g. "A12") — those still require a literal (case-insensitive) match.
 */
function digitsEquivalent(code: string, query: string): boolean {
  return DIGITS_ONLY.test(code) && DIGITS_ONLY.test(query) && stripLeadingZeros(code) === stripLeadingZeros(query);
}

/** Same idea as `digitsEquivalent`, but for the prefix tier: does the
 * zero-stripped query prefix the zero-stripped code? Lets progressive
 * typing ("1" while browsing toward "010"-"019"/"100"-"199") keep working
 * on fixed-width codes, in addition to literal padded-form prefix typing
 * (typing "0" or "01" directly), which the literal check in `tier()`
 * already covers. */
function digitsPrefixEquivalent(code: string, query: string): boolean {
  return (
    DIGITS_ONLY.test(code) && DIGITS_ONLY.test(query) && stripLeadingZeros(code).startsWith(stripLeadingZeros(query))
  );
}

/**
 * Business-code fast-search ranking shared by every SearchCombobox call
 * site (Worker/Product/Operation Daily Entry pickers, Norm creation, Master
 * Data). Three tiers, checked in priority order per item:
 *
 *   1. exact normalized code match — OR, for fixed-width numeric codes
 *      (Worker/Product/Operation), zero-padding-equivalent (query "5"
 *      exact-matches stored "005") so operators never have to type leading
 *      zeroes (NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md §17-18)
 *   2. normalized code PREFIX match (literal, on the padded form) — OR the
 *      zero-padding-equivalent prefix, so progressive typing still narrows
 *      fixed-width codes intuitively
 *   3. name/description match (existing `fuzzyIncludes` behavior, diacritic-
 *      and case-insensitive)
 *
 * An item with no `businessCode` (every pre-existing call site) is only
 * ever evaluated on tier 3 — behavior for those callers is unchanged from
 * the pre-ranking `items.filter(fuzzyIncludes(...))`.
 *
 * Items sharing a tier keep their ORIGINAL relative order (Array.prototype.sort
 * is a stable sort per ECMA-262) — deterministic without assuming Worker
 * codes are unique (they aren't, until the DB constraint from this same
 * change takes effect on existing data).
 *
 * An empty/whitespace-only query returns every item unranked, in original
 * order — identical to the pre-existing empty-needle-matches-everything
 * contract.
 */
export function rankSearchMatches<T extends RankableSearchItem>(items: T[], query: string): T[] {
  if (query.trim() === "") return items;

  const normalizedQuery = normalizeCode(query);

  function tier(item: T): 1 | 2 | 3 | null {
    if (item.businessCode) {
      const normalizedCode = normalizeCode(item.businessCode);
      if (normalizedCode === normalizedQuery || digitsEquivalent(normalizedCode, normalizedQuery)) return 1;
      if (normalizedCode.startsWith(normalizedQuery) || digitsPrefixEquivalent(normalizedCode, normalizedQuery)) {
        return 2;
      }
    }
    if (fuzzyIncludes(item.label, query) || fuzzyIncludes(item.description ?? "", query)) return 3;
    return null;
  }

  return items
    .map((item) => ({ item, tier: tier(item) }))
    .filter((entry): entry is { item: T; tier: 1 | 2 | 3 } => entry.tier !== null)
    .sort((a, b) => a.tier - b.tier)
    .map((entry) => entry.item);
}

/**
 * "CODE — NAME" display convention for Daily Entry pickers and the Master
 * Data table (NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md
 * §7/§10 — e.g. "12 — Petar Petrović"). When `code` is null/undefined/empty,
 * shows the name alone — never a fake placeholder identifier. `code` is
 * inserted verbatim (never parsed/reformatted), so "007" stays "007".
 */
export function formatCodeAndName(code: string | null | undefined, name: string): string {
  return code ? `${code} — ${name}` : name;
}
